import { Icon } from "./Icon";
import { useEffect, useState, useSyncExternalStore } from "react";
import { supabase } from "../lib/supabase";
import { clipsMuted, subscribeClipsMuted } from "../lib/clipsMuted";
import { MediaStage } from "./MediaStage";
import { playLabel } from "../lib/setClips";

/* THE CLIP PLAYER — ONE on the web (W116): `ClipPlayButton` opens its stage (`ClipStage`, the page dimmed behind it) from
 * every surface that prints a logged set's numbers. The inline list player (`ClipPlayer`, `Set N` buttons over an inline
 * `<video>`) that Progress and the review's loose clips used was a SECOND player and is gone.
 *
 * WHO may see a clip is the DATABASE's (migration `20260905120000`: a `form_clips` SELECT for the client's ACTIVE coach,
 * and the private bucket's object read derived from the row); this signs the path it is handed, on demand, and plays it
 * where it is. A path the bucket cannot sign — the object is gone, or the read was refused — reads `Clip unavailable`.
 */

/** `label` — the page's own name for the clip's set, when it numbers sets differently from `set_number` (the review's
 *  table numbers working sets apart from ramps); without one the button says `Set <set_number>`. */
export type PlayableClip = { readonly id: string; readonly storagePath: string; readonly mediaKind: string; readonly setNumber: number | null; readonly label?: string };

export const CLIP_UNAVAILABLE = "Clip unavailable";

/** 🔴 W116 — THE ONE PLAY BUTTON, on every surface that prints a logged set's numbers (his word, 2026-09-30: *"where there
 *  is points (logged workouts) with numbers, there needs to be the videos … just a play button like the coach has when he
 *  reviews a workout"*): the review's own round button, everywhere — the review, the Dashboard's overview window, the
 *  Training tab's day window and the Progress readout. `setLabel` is the line's own name for its set (`2`, `W1`); null on a
 *  summary line. Its name (`aria-label` + `title`) is `setClips.playLabel` — no words are drawn beside the glyph. It
 *  draws NOTHING for an empty list. The 24 px circle keeps the row's height; its 44 px hit area is a `::before` outside
 *  the box (pro.css), which takes taps without taking space.
 *
 *  W93 (his word, 2026-09-28): "in the done, where there's logged videos we will have the play button to play the client
 *  videos" — ONE round play button beside the set's Done value (no `Set N` label: the row already says which set). The
 *  clip is signed on the click and plays BIG in the middle of the screen (`MediaStage`, the page dimmed and blurred behind
 *  it), muted when the coach's `Client videos muted` switch is on. A set with several clips plays them in order: the
 *  stage's `‹ ›` walk them. A path the bucket cannot sign reads `Clip unavailable` in the stage, never a silent nothing. */
export function ClipPlayButton({ clips, setWords, setLabel }: { clips: readonly PlayableClip[]; setWords: string; setLabel: string | null }) {
  const [open, setOpen] = useState<number | null>(null);
  if (clips.length === 0) return null;
  const label = playLabel(setLabel, clips);
  return (
    <>
      <button type="button" className="clip-play" onClick={() => setOpen(0)}
              aria-label={label} title={label}>
        <Icon name="play" size={12} />
        {clips.length > 1 && <span className="clip-play-count">{clips.length}</span>}
      </button>
      {open !== null && <ClipStage clips={clips} index={open} setIndex={setOpen} setWords={setWords} />}
    </>
  );
}

function ClipStage({ clips, index, setIndex, setWords }: {
  clips: readonly PlayableClip[]; index: number; setIndex: (i: number | null) => void; setWords: string;
}) {
  const muted = useSyncExternalStore(subscribeClipsMuted, clipsMuted, clipsMuted);
  const clip = clips[index];
  const [src, setSrc] = useState<{ id: string; url: string } | "failed" | null>(null);
  useEffect(() => {
    if (clip === undefined) return;
    let cancelled = false;
    setSrc(null);
    void supabase.storage.from("form-clips").createSignedUrl(clip.storagePath, 60 * 60).then(({ data, error }) => {
      if (cancelled) return;
      setSrc(error !== null || data === null ? "failed" : { id: clip.id, url: data.signedUrl });
    });
    return () => { cancelled = true; };
  }, [clip]);
  if (clip === undefined) return null;
  return (
    <MediaStage shape="wide" label={`Clip of ${setWords}`} onClose={() => setIndex(null)}>
      {src === "failed" ? <p className="media-stage-note" role="alert">{CLIP_UNAVAILABLE}</p>
        : src === null ? <p className="media-stage-note" aria-live="polite">Loading the clip…</p>
        : clip.mediaKind === "photo" ? <img className="media-stage-media" src={src.url} alt={`Photo of ${setWords}`} />
        : <video key={src.id} className="media-stage-media" src={src.url} controls playsInline autoPlay muted={muted} />}
      {clips.length > 1 && (
        <p className="media-stage-walk">
          <button type="button" className="media-stage-step" disabled={index === 0} onClick={() => setIndex(index - 1)} aria-label="Previous clip">‹</button>
          <span>{String(index + 1)} of {String(clips.length)}</span>
          <button type="button" className="media-stage-step" disabled={index === clips.length - 1} onClick={() => setIndex(index + 1)} aria-label="Next clip">›</button>
        </p>
      )}
    </MediaStage>
  );
}
